import React, { useEffect, useMemo, useState } from 'react';
import { Box, Flex, icons, Stack } from '@tapresearch/ui/components';
import {
  TapAnswer,
  TapQuestion,
  useQualificationAnswerLabels,
  useQuestionTapAnswersList,
  useTapQuestionCrossTabResult,
  useTapQuestionResult,
} from '../../../api';
import {
  DotLoader,
  GraphToggleSwitch,
  QuestionResultWrapper,
} from '../../common';
import useAsset from '../../hook/useAsset';
import {
  FreeTextAnswersList,
  RankingQuestionTable,
  ResultsTableView,
  StackedBarChart,
} from './charts';
import { GroupAnswersSwitch } from './GroupAnswersSwitch';
import { HideEmptySwitch } from './HideEmptySwitch';
import { useCrossTabBarData } from './hooks/useCrossTabBarData';
import { NoResultsFound } from './NoResultsFound';
import { OtherAnswersAccordion } from './OtherAnswersAccordion';
import { QuestionItemComparisons } from './QuestionItemComparisons';
import {
  ComparableTapQuestionDetails,
  DemographicQuestionDetails,
} from './SurveyResultsWrapper';

const GROUPABLE_MC_ANSWERS_COUNT = 8;

interface SurveyQuestionItemResultsProps {
  tapQuestion: TapQuestion['attributes'];
  countryId: string;
  languageId: string;
  comparisonQuestions: [
    Record<string, DemographicQuestionDetails>,
    Record<string, ComparableTapQuestionDetails>
  ];
  filters: Record<string, string[]>;
  campaignResultIdentifier?: string;
}
export const SurveyQuestionItemResults = ({
  tapQuestion,
  countryId,
  languageId,
  comparisonQuestions,
  filters,
  campaignResultIdentifier,
}: SurveyQuestionItemResultsProps) => {
  const asset = useAsset();

  const isRanking =
    tapQuestion.tap_answer_type === asset.constants.TAP_ANSWER_TYPE_RANKING;

  const isFreeText =
    tapQuestion.tap_answer_type === asset.constants.TAP_ANSWER_TYPE_FREE_TEXT;

  const isMessage =
    tapQuestion.tap_answer_type === asset.constants.TAP_ANSWER_TYPE_MESSAGE;

  const isRating =
    tapQuestion.tap_answer_type === asset.constants.TAP_ANSWER_TYPE_SCALE_RANGE;

  const isChartable = ![
    asset.constants.TAP_ANSWER_TYPE_FREE_TEXT,
    asset.constants.TAP_ANSWER_TYPE_MESSAGE,
  ].includes(tapQuestion.tap_answer_type);

  const [crossTabQuestionId, setCrossTabQuestionId] = useState<string>();

  const [usePercentage, setUsePercentage] = useState(true);

  const [isTableView, setIsTableView] = useState(false);

  const [totalRespondents, setTotalRespondents] = useState(null);

  const [isHidingEmptyCrossTab, setIsHidingEmptyCrossTab] = useState(false);

  const [isSwitching, setIsSwitching] = useState(false);

  const [isGroupedAnswers, setIsGroupedAnswers] = useState(false);

  const isDemographicCrossTab = useMemo(
    () => !!crossTabQuestionId && !crossTabQuestionId.startsWith('tq_'),
    [crossTabQuestionId]
  );

  const isTapQuestionCrossTab = useMemo(
    () => !!crossTabQuestionId && crossTabQuestionId.startsWith('tq_'),
    [crossTabQuestionId]
  );

  const { data: results, isLoading: isResultsLoading } = useTapQuestionResult({
    tapQuestionIdentifier: !crossTabQuestionId && tapQuestion.identifier,
    filters,
    groupAnswers: isGroupedAnswers,
    campaignResultIdentifier,
  });

  const { data: crossTabResults, isLoading: isCrossTabLoading } =
    useTapQuestionCrossTabResult({
      tapQuestionIdentifier: crossTabQuestionId && tapQuestion.identifier,
      filters,
      crossTabQuestionId,
      groupAnswers: isGroupedAnswers,
      campaignResultIdentifier,
    });

  const { data: tapAnswersResponse, isLoading: isTapAnswersLoading } =
    useQuestionTapAnswersList(tapQuestion.identifier, campaignResultIdentifier);

  const tapAnswers: TapAnswer[] | undefined = useMemo(() => {
    if (
      !isGroupedAnswers &&
      (!tapAnswersResponse || !!tapAnswersResponse.length || !isRating)
    ) {
      return tapAnswersResponse;
    }

    const formatAsTapAnswer = (id: string, tapQuestionId: number) => {
      return {
        id,
        attributes: {
          id: id as unknown as number,
          option_text: id,
          tap_question_id: tapQuestionId,
        },
      } as TapAnswer;
    };

    if (!isGroupedAnswers && isRating) {
      return [...Array(tapQuestion.scale_range)].map((_, i) => {
        return formatAsTapAnswer(`${i + 1}`, tapQuestion.id);
      });
    }

    // Grouped answers - get labels from the result sets
    const hasResultsLoaded = !!crossTabQuestionId ? crossTabResults : results;
    if (!hasResultsLoaded) return null;

    const labelData = !!crossTabQuestionId
      ? crossTabResults?.attributes?.results?.labels || null
      : Object.keys(results?.attributes?.results?.answers || {});

    return labelData?.map((groupLabel) =>
      formatAsTapAnswer(groupLabel, tapQuestion.id)
    );
  }, [
    isRating,
    tapAnswersResponse,
    tapQuestion,
    results,
    crossTabQuestionId,
    crossTabResults,
    isGroupedAnswers,
  ]);

  const crossTabAnswerLabels = useMemo(() => {
    if (isTapQuestionCrossTab) {
      const crossTabQuestion = comparisonQuestions[1][crossTabQuestionId];
      return crossTabQuestion.answers.reduce<Record<string, string>>(
        (acc, answer) => {
          return {
            ...acc,
            [answer.id.toString()]: answer.text,
          };
        },
        {}
      );
    }
  }, [comparisonQuestions, crossTabQuestionId, isTapQuestionCrossTab]);

  const { data: qualificationLabels, isLoading: isQualificationLabelsLoading } =
    useQualificationAnswerLabels({
      questionId: !!isDemographicCrossTab && crossTabQuestionId,
      countryId,
      languageId,
      campaignResultIdentifier,
    });

  const crossTabLabelsHash = isDemographicCrossTab
    ? qualificationLabels
    : crossTabAnswerLabels;

  const standardBarData = useMemo(
    () =>
      Object.entries(results?.attributes?.results?.answers || {}).map(
        ([answerId, answerValues]) => {
          return {
            answer_id: answerId,
            [answerId.toString()]: answerValues?.percentage || 0,
            [answerId.toString() + '_total']: answerValues?.total,
          };
        }
      ),
    [results]
  );

  const crossTabBarData = useCrossTabBarData({
    crossTabLabelsHash,
    crossTabResults,
    isHidingEmptyCrossTab,
  });

  const otherAnswers = results?.attributes?.results?.other_answers;
  const freeTextAnswers = results?.attributes?.results?.free_text_answers || [];

  const barData = !!crossTabQuestionId ? crossTabBarData : standardBarData;
  const hasLabels =
    !!tapAnswers && (!crossTabQuestionId || !!crossTabLabelsHash);

  const numRows = isRating
    ? Object.keys(barData).length
    : (hasLabels &&
        (!!crossTabQuestionId
          ? Object.keys(crossTabBarData).length
          : tapAnswers.length)) ||
      0;

  const barHeight = 32;
  const barGap = 10;
  const chartHeight = numRows * barHeight + (numRows - 1) * barGap + 100;

  useEffect(() => {
    if (!crossTabQuestionId && results) {
      setTotalRespondents(results?.attributes?.results?.total_respondents || 0);
    }
  }, [crossTabQuestionId, results]);

  useEffect(() => {
    setIsSwitching(true);
  }, [isHidingEmptyCrossTab, isGroupedAnswers, crossTabQuestionId]);

  useEffect(() => {
    if (isSwitching) {
      setTimeout(() => {
        setIsSwitching(false);
      }, 300);
    }
  }, [isSwitching]);

  const isAnyLoading =
    isResultsLoading ||
    isCrossTabLoading ||
    isTapAnswersLoading ||
    isSwitching ||
    isQualificationLabelsLoading;

  const noResponses =
    results?.attributes?.results?.total_respondents === 0 ||
    totalRespondents === 0;

  const graphPercentToggleSwitch = useMemo(
    () => (
      <GraphToggleSwitch
        {...{
          leftItem: {
            icon: icons.fi.Percent,
            label: 'Graph by percentages',
            callback: () => setUsePercentage(true),
          },
          rightItem: {
            icon: icons.fi.Hash,
            label: 'Graph by respondent totals',
            callback: () => setUsePercentage(false),
          },
          selectedItem: usePercentage ? 'left' : 'right',
        }}
      />
    ),
    [usePercentage]
  );

  const graphTableToggleSwitch = useMemo(
    () => (
      <GraphToggleSwitch
        {...{
          leftItem: {
            icon: icons.fi.BarChart2,
            label: 'Show chart view',
            callback: () => setIsTableView(false),
          },
          rightItem: {
            icon: icons.io4.MdGrid,
            label: 'Show table view',
            callback: () => setIsTableView(true),
          },
          selectedItem: !isTableView ? 'left' : 'right',
        }}
      />
    ),
    [isTableView]
  );

  const hideEmptySwitch = useMemo(
    () => (
      <HideEmptySwitch
        isHiding={isHidingEmptyCrossTab}
        isSwitching={isSwitching}
        setIsHiding={setIsHidingEmptyCrossTab}
      />
    ),
    [isHidingEmptyCrossTab, isSwitching]
  );

  const canGroupAnswers = isChartable && !isRanking;

  const groupAnswersSwitch = useMemo(
    () =>
      canGroupAnswers && (
        <GroupAnswersSwitch
          isGrouped={isGroupedAnswers}
          isSwitching={isSwitching}
          isDisabled={
            !isRating &&
            !((tapAnswersResponse?.length || 0) > GROUPABLE_MC_ANSWERS_COUNT)
          }
          setIsGrouped={setIsGroupedAnswers}
          label={isRating ? 'Group answers:' : 'Simplify data:'}
          {...(!isRating && {
            tooltipText: `This toggle simplifies your data by combining any answers beyond ${GROUPABLE_MC_ANSWERS_COUNT} into a single category. Turn off toggle to view answers/data in full.`,
          })}
        />
      ),
    [
      isGroupedAnswers,
      isRating,
      tapAnswersResponse,
      canGroupAnswers,
      isSwitching,
    ]
  );

  if (isMessage) return <QuestionResultWrapper tapQuestion={tapQuestion} />;

  return (
    <QuestionResultWrapper
      tapQuestion={tapQuestion}
      totalRespondents={totalRespondents}
    >
      <Box
        {...{
          w: { base: 'full', md: 'calc(100% - 130px)' },
          ml: { base: 0, md: '65px' },
          px: { base: 4, md: 0 },
        }}
      >
        {isChartable && !isRanking && !!totalRespondents && (
          <Flex
            mt={6}
            align="flex-start"
            flexWrap={{ base: 'wrap', md: 'nowrap' }}
          >
            <QuestionItemComparisons
              compareState={[crossTabQuestionId, setCrossTabQuestionId]}
              comparisonQuestions={comparisonQuestions}
              tapQuestionId={tapQuestion.id.toString()}
            />
            <ChartControls
              isCrossTab={!!crossTabQuestionId}
              graphTableToggleSwitch={graphTableToggleSwitch}
              graphPercentToggleSwitch={graphPercentToggleSwitch}
              hideEmptySwitch={hideEmptySwitch}
              groupAnswersSwitch={groupAnswersSwitch}
            />
          </Flex>
        )}

        {noResponses && !isAnyLoading ? (
          <NoResultsFound />
        ) : (
          <Flex
            justify="space-between"
            direction={{ base: 'column', md: 'row' }}
          >
            {isAnyLoading ? (
              <Flex justify="center" align="center" w="full" h="400px">
                <DotLoader h={10} dotSize="20px" />
              </Flex>
            ) : (
              <>
                {isFreeText ? (
                  <FreeTextAnswersList answers={freeTextAnswers} />
                ) : (
                  barData &&
                  hasLabels && (
                    <Box
                      ml="2px"
                      w={{
                        base: '100%',
                        md: !!crossTabQuestionId
                          ? 'calc(100% - 280px)'
                          : '100%',
                      }}
                      {...(!!crossTabQuestionId && {
                        mt: { base: 0, md: '-60px' },
                      })}
                    >
                      <Box
                        w="full"
                        height={isRanking || isTableView ? 'auto' : chartHeight}
                      >
                        {isRanking && (
                          <Flex
                            justify="space-between"
                            w="full"
                            px={{ base: 4, md: 0 }}
                            mt={{ base: 6, md: 0 }}
                            flexWrap={{ base: 'wrap', md: 'nowrap' }}
                            flexDirection={{
                              base: 'column-reverse',
                              md: 'row',
                            }}
                          >
                            <RankingQuestionTable
                              usePercentage={usePercentage}
                              tapAnswers={tapAnswers}
                              results={results?.attributes?.results?.answers}
                            />
                            <Flex
                              w={{ base: 'full', md: '150px' }}
                              justify="flex-end"
                            >
                              {graphPercentToggleSwitch}
                            </Flex>
                          </Flex>
                        )}
                        {[
                          asset.constants.TAP_ANSWER_TYPE_MULTIPLE_CHOICE,
                          asset.constants.TAP_ANSWER_TYPE_IMAGE_CHOICE,
                          asset.constants.TAP_ANSWER_TYPE_SCALE_RANGE,
                        ].includes(tapQuestion.tap_answer_type) && (
                          <>
                            {isTableView ? (
                              <ResultsTableView
                                tableData={barData}
                                tapAnswers={tapAnswers}
                                usePercentage={usePercentage}
                                {...(!!crossTabQuestionId && {
                                  crossTabLabels: crossTabLabelsHash,
                                })}
                              />
                            ) : (
                              <StackedBarChart
                                isCrossTab={!!crossTabQuestionId}
                                usePercentage={usePercentage}
                                tapAnswers={tapAnswers}
                                isImageChoice={
                                  tapQuestion.tap_answer_type ===
                                  asset.constants.TAP_ANSWER_TYPE_IMAGE_CHOICE
                                }
                                data={barData}
                                legendContainerId={`question-${tapQuestion.id}-legend-container`}
                              />
                            )}
                          </>
                        )}
                      </Box>
                      {!!otherAnswers?.length && (
                        <OtherAnswersAccordion otherAnswers={otherAnswers} />
                      )}
                    </Box>
                  )
                )}
              </>
            )}

            {!!crossTabQuestionId && (
              <Box w="220px" mt={6} pb={16} h="100%" ml={{ base: 4, md: 0 }}>
                <Box
                  data-testid="chart-legend-container"
                  id={`question-${tapQuestion.id}-legend-container`}
                ></Box>
              </Box>
            )}
          </Flex>
        )}
      </Box>
    </QuestionResultWrapper>
  );
};

const ChartControls = ({
  isCrossTab,
  graphTableToggleSwitch,
  graphPercentToggleSwitch,
  hideEmptySwitch,
  groupAnswersSwitch,
}: {
  isCrossTab: boolean;
  graphTableToggleSwitch: JSX.Element;
  graphPercentToggleSwitch: JSX.Element;
  hideEmptySwitch: JSX.Element;
  groupAnswersSwitch?: JSX.Element;
}) => {
  return (
    <Stack
      w={{ base: '180px', md: '280px' }}
      alignItems="flex-end"
      direction={{ base: 'row', md: 'column' }}
      spacing={4}
      ml={{ base: 2, md: 12 }}
      mt={{ base: 5, md: 0 }}
      flexGrow={{ base: 1, md: 0 }}
      {...(isCrossTab && {
        borderBottom: '1px solid',
        borderColor: 'gray.200',
        pb: 4,
      })}
    >
      <Stack
        spacing={4}
        flexGrow={1}
        direction="row"
        justify={{ base: 'flex-start', md: 'space-between' }}
      >
        {graphTableToggleSwitch}
        {isCrossTab && graphPercentToggleSwitch}
      </Stack>

      {groupAnswersSwitch}

      {isCrossTab && hideEmptySwitch}
    </Stack>
  );
};
